"use client";

// Chapitre « Pratiquer » : le challenge « Créer une fiche de présentation »
// de la formation HTML et CSS (partie 14, juste après la partie où l'on
// apprend les liens e-mail). Léa ajoute le lien pour lui écrire, l'aperçu
// se met à jour, puis BeBot vérifie et répond dans la conversation comme
// dans le vrai challenge : pastille « Challenge réussi ! », score sur 100,
// trois points positifs. La jauge du bas porte le repère des 70 points
// qui valident.

import { Fragment } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useAt, useCount, useTyped } from "../demoClock";
import { useTarget } from "../DemoCursor";
import Icon from "../icons";
import { BeBotAvatar } from "../sceneParts";

const T = {
    typeStart: 700,
    buttonShown: 2250,
    verify: 3150,
    verdict: 4650,
};

export const script = {
    duration: 7900,
    still: 6200,
    cursor: [{ at: T.verify, target: "challenge.verify", click: true, move: 900 }],
};

const PASS_MARK = 70;

// La ligne tapée, découpée en morceaux colorés : la frappe révèle les
// morceaux dans l'ordre, chacun avec sa couleur.
function useTypedLine(label) {
    const segments = [
        { text: "<a", color: "text-[#f38ba8]" },
        { text: " href", color: "text-[#89b4fa]" },
        { text: "=", color: "text-[#cdd6f4]" },
        { text: '"mailto:lea@exemple.fr"', color: "text-[#a6e3a1]" },
        { text: ">", color: "text-[#f38ba8]" },
        { text: label, color: "text-[#cdd6f4]" },
        { text: "</a>", color: "text-[#f38ba8]" },
    ];
    const full = segments.map((segment) => segment.text).join("");
    const typed = useTyped(full, T.typeStart, 28);

    let remaining = typed.length;
    const visible = [];
    for (const segment of segments) {
        if (remaining <= 0) break;
        visible.push({ ...segment, text: segment.text.slice(0, remaining) });
        remaining -= segment.text.length;
    }
    return { visible, typing: typed.length > 0 && typed.length < full.length };
}

function Editor({ compact }) {
    const t = useTranslations();
    const { visible, typing } = useTypedLine(t("showcase_challenge_site_cta"));

    const lines = [
        <Fragment key="open">
            <span className="text-[#f38ba8]">&lt;article</span> <span className="text-[#89b4fa]">class</span>
            <span className="text-[#cdd6f4]">=</span>
            <span className="text-[#a6e3a1]">&quot;fiche&quot;</span>
            <span className="text-[#f38ba8]">&gt;</span>
        </Fragment>,
        <Fragment key="img">
            {"  "}
            <span className="text-[#f38ba8]">&lt;img</span> <span className="text-[#89b4fa]">src</span>
            <span className="text-[#cdd6f4]">=</span>
            <span className="text-[#a6e3a1]">&quot;lea.jpg&quot;</span> <span className="text-[#89b4fa]">alt</span>
            <span className="text-[#cdd6f4]">=</span>
            <span className="text-[#a6e3a1]">&quot;{NAME}&quot;</span>
            <span className="text-[#f38ba8]">&gt;</span>
        </Fragment>,
        <Fragment key="name">
            {"  "}
            <span className="text-[#f38ba8]">&lt;h1&gt;</span>
            <span className="text-[#cdd6f4]">{NAME}</span>
            <span className="text-[#f38ba8]">&lt;/h1&gt;</span>
        </Fragment>,
        <Fragment key="job">
            {"  "}
            <span className="text-[#f38ba8]">&lt;p&gt;</span>
            <span className="text-[#cdd6f4]">{t("showcase_challenge_site_job")}</span>
            <span className="text-[#f38ba8]">&lt;/p&gt;</span>
        </Fragment>,
        <Fragment key="link">
            {"  "}
            {visible.map((segment, index) => (
                <span key={index} className={segment.color}>
                    {segment.text}
                </span>
            ))}
            {typing && <span className="ml-px inline-block h-[1.1em] w-[2px] translate-y-[3px] bg-[#cdd6f4]" />}
        </Fragment>,
        <span key="close" className="text-[#f38ba8]">&lt;/article&gt;</span>,
    ];

    return (
        <div
            className={`h-full overflow-hidden bg-[#1e1e2e] font-mono ${
                compact ? "text-[9.5px] leading-[1.7]" : "text-[12.5px] leading-[1.75]"
            }`}
        >
            <div className={`flex items-center gap-2 border-b border-white/5 text-gray-400 ${compact ? "px-3 py-1.5 text-[9px]" : "px-4 py-2 text-[11px]"}`}>
                <Icon name="code" className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
                index.html
            </div>
            <pre className={`mb-0 whitespace-pre ${compact ? "px-3 py-2" : "px-4 py-3"}`}>
                {lines.map((line, index) => (
                    <div key={index} className="flex">
                        <span className={`select-none text-right text-gray-600 ${compact ? "mr-3 w-4" : "mr-4 w-5"}`}>
                            {index + 12}
                        </span>
                        <code className="min-w-0 overflow-hidden text-ellipsis whitespace-pre">{line}</code>
                    </div>
                ))}
            </pre>
        </div>
    );
}

// L'aperçu : la fiche de Léa, telle que son code la rend.
function Preview({ compact }) {
    const t = useTranslations();
    const linkShown = useAt(T.buttonShown);

    return (
        <div className="flex h-full items-center justify-center bg-[#FBF8F3]">
            <div
                className={`flex flex-col items-center rounded-2xl bg-white text-center shadow-[0_8px_24px_-12px_rgba(28,25,23,0.18)] ring-1 ring-black/5 ${
                    compact ? "w-[230px] px-5 py-5" : "w-[300px] px-7 py-7"
                }`}
            >
                <span
                    className={`flex items-center justify-center rounded-full bg-[#E8D5C4] font-sourceSerif text-[#7c4a2d] ${
                        compact ? "h-12 w-12 text-[17px]" : "h-16 w-16 text-[22px]"
                    }`}
                >
                    LM
                </span>
                <p className={`mb-0 font-sourceSerif text-[#1c1917] ${compact ? "mt-3 text-[20px]" : "mt-4 text-[26px]"}`}>
                    {NAME}
                </p>
                <p className={`mb-0 text-[#57534e] ${compact ? "mt-1 text-[11px]" : "mt-1.5 text-[13px]"}`}>
                    {t("showcase_challenge_site_job")}
                </p>
                <div className={compact ? "mt-4 h-7" : "mt-5 h-9"}>
                    <AnimatePresence>
                        {linkShown && (
                            <motion.span
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                className={`inline-flex items-center rounded-full bg-[#1f4e4a] font-semibold text-white ${
                                    compact ? "px-3.5 py-1.5 text-[10.5px]" : "px-5 py-2 text-[13px]"
                                }`}
                            >
                                {t("showcase_challenge_site_cta")}
                            </motion.span>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

function TypingDots() {
    return (
        <span className="flex items-center gap-1 py-1">
            {[0, 1, 2].map((index) => (
                <motion.span
                    key={index}
                    className="h-1.5 w-1.5 rounded-full bg-orange-400"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity, delay: index * 0.16, ease: "easeInOut" }}
                />
            ))}
        </span>
    );
}

const NAME = "Léa Morel";
const POINTS = ["title", "job", "link"];

// La réponse de BeBot, avec la mise en forme du chat des challenges.
function Verdict({ compact }) {
    const t = useTranslations();
    const answered = useAt(T.verdict);

    return (
        <div className="flex items-start gap-2">
            <BeBotAvatar className={compact ? "h-7 w-6" : "h-8 w-7"} />
            <div className="min-w-0 flex-1">
                <p className="mb-1 text-[11px] text-gray-400 dark:text-grayDark-500">BeBot</p>
                <div className="rounded-2xl rounded-tl-sm border border-gray-100 bg-gray-50/80 px-3.5 py-3 dark:border-grayDark-800 dark:bg-white/[0.04]">
                    <AnimatePresence mode="wait" initial={false}>
                        {!answered ? (
                            <motion.div key="dots" exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                                <TypingDots />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="answer"
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-emerald-600 ring-1 ring-emerald-500/20 dark:bg-emerald-400/10 dark:text-emerald-400">
                                        {t("showcase_challenge_passed")}
                                    </span>
                                    <span className="text-[13px] font-bold tabular-nums text-gray-900 dark:text-white">
                                        100/100
                                    </span>
                                </div>
                                <p className={`mb-0 mt-2 leading-relaxed text-gray-700 dark:text-grayDark-200 ${compact ? "text-[11.5px]" : "text-[13px]"}`}>
                                    {t("showcase_challenge_summary")}
                                </p>
                                <ul className="mt-2 space-y-1.5">
                                    {POINTS.map((point, index) => (
                                        <motion.li
                                            key={point}
                                            initial={{ opacity: 0, y: 4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.22, delay: 0.15 + index * 0.08, ease: "easeOut" }}
                                            className={`mb-0 flex items-start gap-1.5 text-gray-600 dark:text-grayDark-300 ${compact ? "text-[11px]" : "text-[12.5px]"}`}
                                        >
                                            <Icon name="check-bold" className="mt-[1px] h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                            {t(`showcase_challenge_point_${point}`)}
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}

function LeftPanel() {
    const t = useTranslations();
    const conversation = useAt(T.verify);

    return (
        <div className="flex h-full flex-col">
            <div className="flex gap-1 border-b border-gray-100 px-4 py-2.5 dark:border-grayDark-800">
                {["instructions", "conversation"].map((tab) => {
                    const active = (tab === "conversation") === conversation;
                    return (
                        <span
                            key={tab}
                            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors duration-200 ${
                                active
                                    ? "bg-gray-100 text-gray-900 dark:bg-white/[0.06] dark:text-white"
                                    : "text-gray-500 dark:text-grayDark-400"
                            }`}
                        >
                            <Icon name={tab} className="h-4 w-4" />
                            {t(`showcase_challenge_tab_${tab}`)}
                        </span>
                    );
                })}
            </div>

            <div className="relative min-h-0 flex-1">
                <AnimatePresence mode="wait" initial={false}>
                    {!conversation ? (
                        <motion.div
                            key="instructions"
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="px-5 py-5"
                        >
                            <p className="mb-0 text-[16px] font-bold text-gray-900 dark:text-white">
                                {t("showcase_challenge_title")}
                            </p>
                            <p className="mb-0 mt-2 text-[13px] leading-relaxed text-gray-600 dark:text-grayDark-300">
                                {t("showcase_challenge_brief")}
                            </p>
                            <p className="mb-0 mt-5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-grayDark-500">
                                {t("showcase_challenge_criteria")}
                            </p>
                            <ul className="mt-2.5 space-y-2">
                                {POINTS.map((point) => (
                                    <li
                                        key={point}
                                        className="mb-0 flex items-center gap-2.5 text-[13px] text-gray-700 dark:text-grayDark-200"
                                    >
                                        <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-200 dark:border-grayDark-700" />
                                        {t(`showcase_challenge_criterion_${point}`)}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="conversation"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                            className="px-4 py-5"
                        >
                            <Verdict />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

function Toolbar({ compact }) {
    const t = useTranslations();
    const verifyRef = useTarget("challenge.verify");
    const verifying = useAt(T.verify);
    const answered = useAt(T.verdict);
    const score = useCount(T.verdict, T.verdict + 700, 0, 100);

    return (
        <div
            className={`flex shrink-0 items-center justify-between border-t border-gray-100 bg-white dark:border-grayDark-800 dark:bg-bgDark-800 ${
                compact ? "h-14 px-4" : "h-14 px-5"
            }`}
        >
            <div className="flex items-center gap-3">
                <div className={`relative h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-grayDark-800 ${compact ? "w-20" : "w-28"}`}>
                    <div
                        className="absolute inset-y-0 left-0 bg-emerald-500 transition-[width] duration-100"
                        style={{ width: `${answered ? score : 0}%` }}
                    />
                    <span
                        className="absolute inset-y-0 w-px bg-gray-400/70 dark:bg-grayDark-500"
                        style={{ left: `${PASS_MARK}%` }}
                    />
                </div>
                {answered ? (
                    <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[12px] font-bold tabular-nums text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400">
                        {score}/100
                    </span>
                ) : (
                    <span className="text-[12px] text-gray-400 dark:text-grayDark-500">
                        {t("showcase_challenge_not_verified")}
                    </span>
                )}
            </div>
            <span
                ref={verifyRef}
                className={`flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 font-semibold text-white shadow-sm ${
                    compact ? "px-3.5 py-2 text-[12px]" : "px-4 py-2 text-[13px]"
                } ${verifying && !answered ? "opacity-80" : ""}`}
            >
                {verifying && !answered ? (
                    <motion.span
                        className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                    />
                ) : (
                    <Icon name="check" className="h-4 w-4" />
                )}
                {verifying && !answered ? t("showcase_challenge_verifying") : t("showcase_challenge_verify")}
            </span>
        </div>
    );
}

function Header({ compact }) {
    const t = useTranslations();
    return (
        <header
            className={`flex shrink-0 items-center gap-2.5 border-b border-gray-100 dark:border-grayDark-800 ${
                compact ? "h-12 px-4" : "h-12 px-5"
            }`}
        >
            <Icon name="close" className="h-5 w-5 text-gray-400 dark:text-grayDark-500" />
            <Icon name="fire" className="h-4 w-4 text-orange-500" />
            <span className={`truncate font-semibold text-gray-900 dark:text-white ${compact ? "text-[13px]" : "text-[14px]"}`}>
                {t("showcase_challenge_title")}
            </span>
            {!compact && (
                <span className="text-[12px] text-gray-400 dark:text-grayDark-500">
                    HTML &amp; CSS · {t("showcase_course_part", { number: 14 })}
                </span>
            )}
        </header>
    );
}

export default function ChallengeScene({ compact }) {
    const verifying = useAt(T.verify);

    if (compact) {
        return (
            <div className="relative flex h-full flex-col bg-white dark:bg-bgDark-800">
                <Header compact />
                <div className="min-h-0 flex-1 space-y-3 overflow-hidden p-4">
                    <div className="h-[226px] overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-grayDark-700">
                        <Preview compact />
                    </div>
                    <div className="h-[142px] overflow-hidden rounded-xl">
                        <Editor compact />
                    </div>
                </div>
                <Toolbar compact />

                {/* Sur mobile, la réponse de BeBot monte du bas de l'écran. */}
                <AnimatePresence>
                    {verifying && (
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            transition={{ type: "spring", damping: 34, stiffness: 380, mass: 0.8 }}
                            className="absolute inset-x-0 bottom-0 z-10 rounded-t-2xl border-t border-gray-100 bg-white px-4 pb-5 pt-2.5 shadow-[0_-12px_32px_-12px_rgba(15,23,42,0.25)] dark:border-grayDark-800 dark:bg-bgDark-800"
                        >
                            <span className="mx-auto mb-3 block h-1 w-9 rounded-full bg-gray-200 dark:bg-grayDark-700" />
                            <Verdict compact />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col bg-white dark:bg-bgDark-800">
            <Header />
            <div className="flex min-h-0 flex-1">
                <aside className="w-[372px] shrink-0 border-r border-gray-100 dark:border-grayDark-800">
                    <LeftPanel />
                </aside>
                <div className="flex min-w-0 flex-1 flex-col">
                    <div className="h-[208px] shrink-0">
                        <Editor />
                    </div>
                    <div className="min-h-0 flex-1 border-t border-gray-100 dark:border-grayDark-800">
                        <Preview />
                    </div>
                </div>
            </div>
            <Toolbar />
        </div>
    );
}
